/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { IModuleProps, INodeProps, NodeTag } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IMoeArticleListItemData } from './moe-article-list-item.data';
import { IHeadingData, IMoeArticleListItemProps } from './moe-article-list-item.props.autogenerated';

export interface IMoeArticleListItemViewProps extends IMoeArticleListItemProps<IMoeArticleListItemData> {
    heading?: React.ReactNode;
    paragraph?: React.ReactNode;
    links?: React.ReactNode;
    backgroundImage?: React.ReactNode;
    thumbnailImage?: React.ReactNode;
    articleListItem: IModuleProps;
    articleListItemContainer: INodeProps;
    articleListItemImageContainer: INodeProps;
    articleListItemThumbnailImageContainer: INodeProps;
    articleListItemHeadingContainer: INodeProps;
}

/**
 *
 * MoeArticleListItem component
 * @extends {React.PureComponent<IMoeArticleListItemProps<IMoeArticleListItemData>>}
 */
class MoeArticleListItem extends React.PureComponent<IMoeArticleListItemProps<IMoeArticleListItemData>> {
    public constructor(props: IMoeArticleListItemProps<IMoeArticleListItemData>) {
        super(props);
    }

    public render(): JSX.Element {
        const { heading, paragraph, thumbnailImage, redirectionUrl } = this.props.config;

        const articleListItemLinkContainer = {
            tag: 'div' as NodeTag,
            className: 'ms-article-list__item-container',
            role: 'link',
            href: redirectionUrl?.linkUrl.destinationUrl,
            'aria-label': redirectionUrl?.ariaLabel
        };

        const articleListItemButtonContainer = {
            tag: 'button' as NodeTag,
            className: 'ms-article-list__item-container'
        };
        const viewProps: IMoeArticleListItemViewProps = {
            ...this.props,
            heading: this._createHeading(heading),
            paragraph: this._createParagraph(paragraph),
            thumbnailImage: this._renderThumbnailImage(thumbnailImage),
            articleListItem: {
                moduleProps: this.props,
                className: ''
            },
            articleListItemContainer: redirectionUrl ? articleListItemLinkContainer : articleListItemButtonContainer,
            articleListItemImageContainer: {
                className: 'ms-article-list__item-image'
            },
            articleListItemThumbnailImageContainer: {
                className: 'ms-article-list__item-thumbnail'
            },
            articleListItemHeadingContainer: {
                className: 'ms-article-list__item-content'
            }
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handles heading content change event.
     * @param event - Content editable event.
     */
    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    /**
     * Handles paragraph content change event.
     * @param event - Content editable event.
     */
    public handleParagraphChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.paragraph = event.target.value;
    };

    /**
     * Handles paragraph content change event.
     * @param linkIndex - Index of link in linkarray.
     * @returns Void.
     */

    private _createParagraph(text?: Msdyn365.RichText): React.ReactNode | null {
        if (!text) {
            return null;
        }
        return (
            <Msdyn365.RichTextComponent
                text={text.length > 180 ? `${text?.substring(0, 180)}...` : text}
                className='ms-article-list__item-text'
                editProps={{ onEdit: this.handleParagraphChange, requestContext: this.props.context.request }}
            />
        );
    }

    private _createHeading(heading?: IHeadingData): React.ReactNode | null {
        if (!heading) {
            return null;
        }
        if (StringExtensions.isNullOrWhitespace(heading.text)) {
            return null;
        }

        return (
            <Msdyn365.Text
                tag={heading.tag ?? 'h3'}
                text={heading.text}
                className='ms-article-list__item-title'
                editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
            />
        );
    }

    private _renderThumbnailImage(
        item?: Msdyn365.IImageData,
        itemImageSettings?: Msdyn365.IImageSettings,
        isAriaHiddenAttribute?: boolean
    ): React.ReactNode | null {
        if (!item) {
            return null;
        }
        if (StringExtensions.isNullOrWhitespace(item.src)) {
            return null;
        }

        return (
            <Msdyn365.Image
                {...item}
                gridSettings={this.props.context.request.gridSettings!}
                imageSettings={itemImageSettings}
                loadFailureBehavior='default'
                aria-hidden={isAriaHiddenAttribute}
                editProps={{
                    key: this.props.config.thumbnailImage ?? {},
                    requestContext: this.props.context.request,
                    moduleType: this.props.typeName,
                    imagePropertyName: 'image',
                    // eslint-disable-next-line max-len -- Auto-suppressed.
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- Auto-suppressed.
                    layout: (this.props.config as any).msdyn365__moduleLayout
                }}
            />
        );
    }
}

export default MoeArticleListItem;
